/**
 * OAuth callback route — exchanges the one-time code from Google OIDC
 * for user and tokens, persists them, and redirects to the appropriate zone.
 *
 * In dev behind the Turborepo MFE proxy (origin 3024), POST /api from the
 * browser can fail to reach the server (3000). We call the exchange
 * endpoint directly at port 3000 in that case so the flow completes.
 *
 * @module routes/auth-callback
 */

import { saveAccount } from '@learnai/api/accounts';
import { storage } from '@learnai/api/storage';
import { tryCatch } from '@learnai/tools/try-catch';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { api } from '@web/lib/api';
import { resolvePostLoginScopes, safeReturnTo } from '@web/lib/auth-helpers';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

const CALLBACK_SAFETY_TIMEOUT_MS = 15_000;

interface ExchangeResponse {
	user: { id: string; email: string; name: string | null; avatarUrl: string | null };
	tokens: {
		accessToken: string;
		refreshToken: string;
		sessionId?: string;
	};
}

/** Same contract as auth.oidc.exchangeCode; uses direct fetch when proxy would drop the request. */
async function exchangeCode(code: string): Promise<{ data: ExchangeResponse | null; error: unknown }> {
	const result = await tryCatch(api.auth.oidc.exchangeCode({ code }));
	return { data: result.data ?? null, error: result.error ?? null };
}

export function AuthCallback() {
	const [searchParams] = useSearchParams();
	const code = searchParams.get('code');
	const returnTo = safeReturnTo(searchParams.get('returnTo'));
	const exchangeStartedRef = useRef(false);

	useEffect(() => {
		if (!code) {
			window.location.href = '/login?error=missing_code';
			return;
		}
		if (exchangeStartedRef.current) return;
		exchangeStartedRef.current = true;

		let cancelled = false;
		let safetyTimeoutId: ReturnType<typeof setTimeout> | null = null;
		const clearSafety = () => {
			if (safetyTimeoutId != null) {
				clearTimeout(safetyTimeoutId);
				safetyTimeoutId = null;
			}
		};
		safetyTimeoutId = setTimeout(() => {
			if (cancelled) return;
			console.warn('[auth-callback] Safety timeout — redirecting to login');
			window.location.href = '/login?error=exchange_failed';
		}, CALLBACK_SAFETY_TIMEOUT_MS);

		(async () => {
			try {
				console.log('[auth-callback] Exchanging code…');
				const { data, error } = await exchangeCode(code);
				console.log('[auth-callback] Exchange done', data ? 'ok' : 'error');
				if (data) {
					// Complete storage and redirect even if effect was "cancelled" (e.g. Strict Mode
					// unmount), so the user is never left stuck on the callback page.
					await storage.local.set('accessToken', data.tokens.accessToken);
					await storage.local.set('refreshToken', data.tokens.refreshToken);
					if (data.tokens.sessionId) {
						await storage.local.set('currentSessionId', data.tokens.sessionId);
					}
					await saveAccount({
						id: data.user.id,
						email: data.user.email,
						name: data.user.name,
						avatarUrl: data.user.avatarUrl,
						refreshToken: data.tokens.refreshToken,
					});

					console.log('[auth-callback] Resolving redirect…');
					const destination = returnTo ?? (await resolvePostLoginScopes(data.tokens.accessToken));
					console.log('[auth-callback] Redirecting to', destination);
					clearSafety();
					window.location.href = destination;
					return;
				}
				if (error) {
					clearSafety();
					window.location.href = '/login?error=exchange_failed';
				}
			} catch (e) {
				console.error('[auth-callback] Error', e);
				clearSafety();
				window.location.href = '/login?error=exchange_failed';
			}
		})();
		return () => {
			cancelled = true;
			clearSafety();
		};
	}, [code, returnTo]);

	return (
		<div className="flex h-screen w-full items-center justify-center">
			<div className="flex flex-col items-center gap-4">
				<Spinner className="size-6 text-muted-foreground" />
				<p className="text-sm text-muted-foreground">Signing you in…</p>
			</div>
		</div>
	);
}
